/**
 * @typedef {Object} KycStatusVM
 * @property {boolean} hasPan
 * @property {string|null} panStatus
 * @property {boolean} hasBank
 * @property {string|null} bankStatus
 */

// mentorKycDocuments doesn't have its own "original filename" column, but the backend embeds
// it in the S3 key at upload time: uploadHelper.js's buildKey() writes
// `${keyPrefix}/${folder}/${subfolder}${Date.now()}-${nanoid(8)}-${safeName}`, where safeName
// is the mentor's real original filename with non [\w.-] characters swapped for underscores.
// Parse that back out instead of showing a made-up label.
const ORIGINAL_FILENAME_PATTERN = /^\d+-[A-Za-z0-9_-]{8}-(.+)$/;

const getDocumentName = (url) => {
  if (!url) return undefined;
  const path = url.split("?")[0];
  const keySegment = decodeURIComponent(path.split("/").pop() || "");
  const match = keySegment.match(ORIGINAL_FILENAME_PATTERN);
  return match ? match[1] : keySegment;
};

/**
 * Raw GET /mentor/:mentorId/kyc-details response → view model for the
 * "Action required" banner and the "Edit details" routing logic.
 *
 * @param {object} raw
 * @returns {KycStatusVM}
 */
export const mapKycStatus = (raw) => {
  const panStatus = raw?.pan?.status ?? null;
  const bankStatus = raw?.bank?.status ?? null;
  return {
    hasPan: !!panStatus && panStatus !== "not_submitted",
    panStatus: panStatus && panStatus !== "not_submitted" ? panStatus : null,
    hasBank: !!bankStatus && bankStatus !== "not_submitted",
    bankStatus:
      bankStatus && bankStatus !== "not_submitted" ? bankStatus : null,
  };
};

/**
 * Raw GET /mentor/:mentorId/kyc/pan-details response → prefill view model
 * for EditPanDetailsModal's currentDetails prop. `reason` is only present
 * when havingPan is "no" (the mentor's previously-given reason for not
 * having a PAN) — undefined otherwise. panNumber comes back fully decrypted
 * (not masked) — the backend returns it as-is to the owning mentor.
 * panDocumentUrl/form60DocumentUrl are pulled out of the raw `documents`
 * array by documentType — whichever one wasn't uploaded is undefined.
 * @param {object} raw
 * @returns {{ havingPan: string, nameAsPerPan: string, dobAsPerPan: string, reason: string, panNumber: string, panDocumentUrl: string, panDocumentName: string, form60DocumentUrl: string, form60DocumentName: string }}
 */
export const mapPanDetails = (raw) => {
  const panDoc = raw?.documents?.find((doc) => doc.documentType === "pan_copy");
  const form60Doc = raw?.documents?.find(
    (doc) => doc.documentType === "form_60",
  );
  return {
    havingPan: raw?.havingPan,
    nameAsPerPan: raw?.nameAsPerPan,
    dobAsPerPan: raw?.dobAsPerPan,
    reason: raw?.reason,
    panNumber: raw?.panNumber,
    panDocumentUrl: panDoc?.url,
    panDocumentName: getDocumentName(panDoc?.url),
    form60DocumentUrl: form60Doc?.url,
    form60DocumentName: getDocumentName(form60Doc?.url),
  };
};

/**
 * Raw GET /mentor/:mentorId/kyc/bank-details response → prefill view model
 * for EditBankDetailsModal's currentDetails prop. accountNumber comes back
 * fully decrypted (not masked) — the backend returns it as-is to the owning
 * mentor. bankProofDocumentUrl is pulled out of the raw `documents` array.
 * @param {object} raw
 * @returns {{ accountHolderName: string, mobileNumber: string, ifscCode: string, bankName: string, branchName: string, accountType: string, accountNumber: string, bankProofDocumentUrl: string, bankProofDocumentName: string }}
 */
export const mapBankDetails = (raw) => {
  const bankProofDoc = raw?.documents?.find(
    (doc) => doc.documentType === "bank_proof",
  );
  return {
    accountHolderName: raw?.accountHolderName,
    mobileNumber: raw?.mobileNumber,
    ifscCode: raw?.ifscCode,
    bankName: raw?.bankName,
    branchName: raw?.branchName,
    accountType: raw?.accountType,
    accountNumber: raw?.accountNumber,
    bankProofDocumentUrl: bankProofDoc?.url,
    bankProofDocumentName: getDocumentName(bankProofDoc?.url),
  };
};
